import { HttpDownloadProgressEvent, HttpErrorResponse, HttpEvent, HttpEventType, HttpHandlerFn, HttpRequest, HttpUploadProgressEvent } from '@angular/common/http'
import { inject } from '@angular/core'
import { BehaviorSubject, Observable, throwError } from 'rxjs'
import { catchError, tap } from 'rxjs/operators'
import { HttpStatusService } from '../../../services/http-status/http-status.service'

let incrementTimer: ReturnType<typeof setTimeout> = null

/**
 * Intercept the HTTP Request and track the progress of the request.
 *
 * @param req   Current HTTP Request that is being made
 * @param next  HTTP Handler that will take over when the request is complete
 */
export function httpStatusInterceptor(req: HttpRequest<unknown>, next: HttpHandlerFn): Observable<HttpEvent<unknown>> {
  let progress = inject(HttpStatusService).progress

  let showStatus = req.headers.get('Client-Show-Status')
  showStatus = `${ showStatus }`.toUpperCase()

  // Check if the request has the loading bar enabled
  if (showStatus === 'TRUE') {
    return next(req)
      .pipe(
        tap((event) => httpEventProgress(event, progress)),
        catchError((error) => httpErrorProgress(error, progress))
      ) as Observable<HttpEvent<unknown>>
  }

  return next(req)
}

/**
 * Complete the loading progress and re-throw the captured error
 *
 * @param error           HTTP Error response from request to be re-thrown
 * @param progress        The observable progress of the HTTP request
 * @param incrementTimer  The active timer used to delay the progress
 */
function httpErrorProgress(
  error: HttpErrorResponse,
  progress: BehaviorSubject<number>
): Observable<unknown> {
  if (incrementTimer) {
    clearTimeout(incrementTimer)
  }

  progress.next(1)

  return throwError(error)
}

/**
 * Increment the progress based on the type of HTTP Event currently being observed
 *
 * @param event           Based on this event's type, we will increment the progress
 * @param progress        The observable progress of the HTTP request
 * @param incrementTimer  The active timer used to delay the progress
 */
function httpEventProgress(
  event: HttpEvent<unknown>,
  progress: BehaviorSubject<number>
) {
  switch (event.type) {
    case HttpEventType.Sent: {
      progress.next(0)
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      incrementTimer = setTimeout(() => {
        progress.next(0.25)
      }, 5)
      break
    }

    case HttpEventType.ResponseHeader: {
      progress.next(0.50)
      break
    }

    case HttpEventType.UploadProgress: {
      let uploadProgress = loadProgress(event)
      progress.next(uploadProgress)
      break
    }

    case HttpEventType.DownloadProgress: {
      let downloadProgress = loadProgress(event)
      progress.next(downloadProgress)
      break
    }

    case HttpEventType.Response: {
      progress.next(1)
      break
    }
  }
}

/**
 * Adjust the progress based on the percentage of download or upload for a request
 *
 * @param event Http Event containing the total amount sent, and how much has been loaded
 */
function loadProgress(event: HttpDownloadProgressEvent | HttpUploadProgressEvent): number {
  let percentLoaded = event.total ? (event.loaded / event.total) * 0.25 : 0
  return 0.75 + percentLoaded
}
